import React, { type FC, useEffect, useState } from "react";
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import IconDelete from "@mui/icons-material/DeleteOutline";
import IconWarning from "@mui/icons-material/Report";
import IconEdit from "@mui/icons-material/Edit";
import { useQuery } from "@apollo/client";
import { useFormik } from "formik";

import { Button, Select, TextBox } from "components/Shared/Inputs";
import { type EmergencyContactFormProps, type EmergencyContactSubmitHandler } from "./interfaces";
import { CONTACT_RELATIONSHIP_QUERY } from "../../queries";
import { type IEmergencyContact } from "components/CaseManagement/interfaces";
import { Spinner } from "components/Shared";
import * as Yup from 'yup';
import { useTranslation } from "react-i18next";

const ErrorText = ({ error }) => {
    return error && (
        <span style={{ fontSize: '12px', color: 'red', display: 'flex', alignItems: 'center' }}>
            <IconWarning style={{ height: '16px' }} />{error}
        </span>
    )
}

export const EmergencyContactForm: FC<EmergencyContactFormProps> = React.memo(({
    emergencyContact = {} as IEmergencyContact,
    title,
    position = "first",
    readonly,
    disabled,
    hideActionButtons,
    handleSubmit,
    setFormikContext,
    onRemove
}) => {
    const { i18n, t } = useTranslation(['emergencyContact', 'common'], { useSuspense: false });
    const MAX_NAME_LENGTH = 50;
    const MAX_PHONE_LENGTH = 11;
    const { loading, data: relationshipData } = useQuery(CONTACT_RELATIONSHIP_QUERY, {
        fetchPolicy: "cache-first",
        variables: { language: i18n.language.substring(0,2) }
    });
    const [isReadonly, setIsReadonly] = useState(readonly || disabled);
    const phoneRegExp = /^\d+$/;

    const relationships = relationshipData?.contactRelationships;
    const otherRelationshipId = relationships?.find(rel => ["other", "autre"].includes(rel.description.toLowerCase()))?.contactRelationshipId;

    const EmergencyContactSchema = Yup.object().shape({
        firstName: Yup.string()
                    .max(MAX_NAME_LENGTH, t('first_name_cannot_be_more_than_x_chars', { replace: { string_length: MAX_NAME_LENGTH } }))
                    .required(t('first_name_is_required')),
        lastName: Yup.string()
                    .max(MAX_NAME_LENGTH, t('last_name_cannot_be_more_than_x_chars', { replace: { string_length: MAX_NAME_LENGTH } }))
                    .required(t('last_name_is_required')),
        phoneNumber: Yup.string()
                        .max(MAX_PHONE_LENGTH, t('phone_number_cannot_be_more_than_x_chars', { replace: { string_length: MAX_PHONE_LENGTH }}))
                        .matches(phoneRegExp, t('phone_number_is_not_valid'))
                        .required(t('phone_number_is_required')),
        contactRelationshipId: Yup.number().required(t('relationship_is_required')),
        otherRelationship: Yup.string()
                            .max(MAX_NAME_LENGTH, t('nature_of_relationship_cannot_be_more_than_x_chars', { replace: { string_length: MAX_NAME_LENGTH }}))
                            .nullable()
                            .when('contactRelationshipId', {
                                is: otherRelationshipId,
                                then: (schema) => schema.required(t('nature_of_relationship_is_required')),
                                otherwise: (schema) => schema.optional()
                            })
    });

    const formik = useFormik<IEmergencyContact>({
        initialValues: emergencyContact,
        onSubmit: handleSubmit as EmergencyContactSubmitHandler,
        validateOnBlur: true,
        validationSchema: EmergencyContactSchema,
    });
    
    const handleUpdate = () => setIsReadonly(true);
    const handleCancel = () => {
        formik.resetForm();
        setIsReadonly(true);
    }

    // call the setter on mount
    useEffect(() => {
        setFormikContext?.(formik);
        return () => setFormikContext?.(null);
    }, [])

    // call the setter whenever values change
    useEffect(() => {
        if (formik) setFormikContext?.(formik);
    }, [formik.values, formik.isValid]);

    // if readonly prop changes, we need to update isReadonly state
    useEffect(() => {
        setIsReadonly(readonly || disabled);
    }, [readonly, disabled])

    const isFirst = position === "first";

    return (
        <Grid container component='form' onSubmit={formik.handleSubmit} spacing={3} className='emergency_contact_form'>
            <fieldset>
            {title && <legend><h3>{title}</h3></legend>}
            {
                <>
                    {!disabled &&
                        <Grid container>
                            {!isFirst && <Grid item xs={isReadonly ? 11 : 12} style={{ textAlign: 'right' }}><Button variant="text" round  onClick={onRemove}><IconDelete color="error" /></Button></Grid>}
                            {isReadonly && <Grid item xs={isFirst ? 12 : 1} style={{ textAlign: 'right' }}><Button variant="text" round onClick={() => setIsReadonly(false)} ><IconEdit color="primary" /></Button></Grid>}
                        </Grid>
                    }
                    <Grid item xs={12} container spacing={2}>
                        <Grid item xs={12}>
                            <TextBox id="firstName" value={formik.values?.firstName} readOnly={isReadonly} label={t("first_name")} onChange={formik.handleChange} fullWidth error={Boolean(formik?.errors.firstName)} />
                            <ErrorText error={formik.errors.firstName} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextBox id="lastName" value={formik.values?.lastName} readOnly={isReadonly} label={t("last_name")} onChange={formik.handleChange} fullWidth error={Boolean(formik?.errors.lastName)} />
                            <ErrorText error={formik.errors.lastName} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextBox id="phoneNumber" value={formik.values?.phoneNumber} readOnly={isReadonly} label={t("phone_number")} onChange={formik.handleChange} fullWidth error={Boolean(formik?.errors.phoneNumber)} />
                            <ErrorText error={formik.errors.phoneNumber} />
                        </Grid>
                        <input type="hidden" id="status" name="status" value={formik.values?.status} />
                    </Grid>
                    <Grid item xs={12} container spacing={2}>
                        <Grid item md={6} xs={12}>
                            <Select label={t("relationship")} name="contactRelationshipId"
                                value={formik.values?.contactRelationshipId || ''} onChange={formik.handleChange}
                                id="contactRelationshipId" error={Boolean(formik.errors?.contactRelationshipId)}
                                className="provider_details_input" fullWidth readOnly={isReadonly}
                            >
                                {
                                    relationships?.map(rel => (
                                        <MenuItem value={rel.contactRelationshipId} key={rel.contactRelationshipId}>
                                            {rel.description}
                                        </MenuItem>
                                    ))
                                }
                            </Select>
                            <ErrorText error={formik.errors.contactRelationshipId} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {formik.values?.contactRelationshipId === otherRelationshipId && (
                                <>
                                    <TextBox id="otherRelationship" value={formik.values?.otherRelationship} label={t('nature_of_relationship')} readOnly={isReadonly} onChange={formik.handleChange} fullWidth error={Boolean(formik?.errors.otherRelationship)} />
                                    <ErrorText error={formik.errors.otherRelationship} />
                                </>
                            )}
                            {loading && <Spinner />}
                        </Grid>
                    </Grid>
                    {  
                    (!isReadonly && !hideActionButtons) && (
                        <Grid item xs={12} container spacing={2}>
                            <Grid item>
                                <Button type="submit" variant="opaque" onClick={handleUpdate}>
                                    {t('save_contact')}
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button type="button" variant="outline" onClick={handleCancel}>{t('common:cancel')}</Button>
                            </Grid>
                        </Grid>
                    )}
                </>
            }  
            </fieldset>
        </Grid>
    );
});
